/*!
 * beat-grid.js — СЕТКА ДОЛЕЙ живой записи: когда именно падает каждая доля.
 *
 * Зачем отдельно от bpm-detector.js. Тот отвечает на вопрос «какой темп» одним
 * числом и одной фазой, и его собственная документация говорит, что из них
 * выходит: «a grid stretched across a whole song drifts seconds by the end —
 * 4 % at 124 BPM is 7.7 s over three minutes». Для вывески «106 BPM» этого
 * достаточно. Для того, чтобы ПОЛОЖИТЬ что-то поверх чужой записи — нет:
 * добавленная партия уедет, и услышит это каждый.
 *
 * Замер (tests/playwright/beat-grid-locks-to-the-music.mjs, 16.09.2026),
 * доля долей, попавших в 50 мс от реального удара, 12 живых треков:
 *
 *     сетка из темпа и фазы     32…58 %   (медиана 42)
 *     эта сетка                 63…99 %   (медиана 94)
 *
 * На синтетике с ИЗВЕСТНЫМИ долями и плывущим темпом (120 → 126 за минуту)
 * ошибка падает с 247 мс до 6; на ровном темпе — со 100 мс до 7.
 *
 * Как устроено: динамическое программирование по огибающей онсетов (Ellis,
 * 2006). Каждая доля выбирается так, чтобы попасть в громкое место И не уйти
 * далеко от ожидаемого интервала — штраф логарифмический, поэтому интервал
 * может ПЛЫТЬ вместе с музыкой, а не держаться числа, посчитанного в начале.
 *
 * ⚠️ Метрический уровень — отдельная задача, и она решена не до конца.
 * Счёт сетки делится на число долей (иначе побеждает самая частая), и это
 * смещает выбор к половинному темпу: 54 вместо 106. Лечится тем же
 * логарифмическим приоритетом темпа, что стоит внутри самого детектора
 * (центр 120, sigma log2 = 1) — после него уровень верен на 8 треках из 12
 * замера. Три оставшихся (рок-баллада, панк, метал) дают 63…91 % попадания
 * при НЕ ТОМ уровне: сетка на настоящих ударах, но не там, где их считает
 * человек. Поэтому `track()` возвращает `alignment` и `bpm` — тул обязан
 * показать темп и дать его исправить, а не делать вид, что угадал.
 *
 *   var g = window.BeatGrid.track(audioBuffer);
 *   // { beats: [0.51, 1.07, …], bpm: 106, alignment: 0.94, level: 1 }
 *   var g2 = window.BeatGrid.track(audioBuffer, { bpm: 212 });  // человек поправил
 */
(function () {
    'use strict';

    /* Сколько метрических уровней перебирать вокруг подсказки. Шире не нужно:
       ошибка детектора в этом корпусе всегда степень двойки от истины. */
    var LEVELS = [0.25, 0.5, 1, 2, 4];
    var BPM_MIN = 50, BPM_MAX = 210;
    /* Доля считается попавшей, если реальный удар не дальше этого. 50 мс —
       порог, за которым слышно, что «вместе» стало «вразнобой». */
    var HIT_S = 0.05;
    /* Жёсткость привязки к ожидаемому интервалу. 100 — из работы Ellis; при
       меньшем сетка начинает гоняться за каждым синкопом, при большем
       перестаёт следовать за уводом темпа. */
    var TIGHTNESS = 100;

    function envelopeOf(ab) {
        return (typeof window.__onsetEnvelope === 'function') ? window.__onsetEnvelope(ab) : null;
    }

    /* ── DP: последовательность долей по огибающей ─────────────────────────── */
    function trackAt(env, periodSec) {
        var flux = env.flux, n = flux.length, fps = env.dsr / env.hop;
        if (!(n > 8)) return null;

        var mx = 0, i;
        for (i = 0; i < n; i++) if (flux[i] > mx) mx = flux[i];
        if (!(mx > 0)) return null;

        var P = periodSec * fps;
        if (!(P > 1) || P * 4 > n) return null;
        var lo = Math.max(1, Math.round(P * 0.5));
        var hi = Math.min(n - 1, Math.round(P * 2));

        var C = new Float64Array(n), prev = new Int32Array(n);
        for (i = 0; i < n; i++) {
            var best = -Infinity, bestM = -1;
            for (var m = i - hi; m <= i - lo; m++) {
                if (m < 0) continue;
                var r = Math.log((i - m) / P);
                var s = C[m] - TIGHTNESS * r * r;
                if (s > best) { best = s; bestM = m; }
            }
            var o = flux[i] / mx;
            C[i] = bestM < 0 ? o : o + best;
            prev[i] = bestM;
        }

        /* Обратный ход — с лучшего кадра в последнем периоде: конец трека может
           быть тише, и брать просто последний кадр значит терять хвост. */
        var tail = -1, tailScore = -Infinity;
        for (i = Math.max(0, n - Math.round(P)); i < n; i++) {
            if (C[i] > tailScore) { tailScore = C[i]; tail = i; }
        }
        if (tail < 0) return null;

        var frames = [];
        for (i = tail; i >= 0; i = prev[i]) {
            frames.push(i);
            if (prev[i] < 0) break;
        }
        frames.reverse();
        if (frames.length < 4) return null;

        /* Время кадра — ЦЕНТР его окна, а не начало: та же поправка, что в
           beatOffset детектора (там она выведена замером по одиночным кликам,
           27–37 мс, и fftN/2/dsr = 32.0 мс её объясняет). */
        var half = (env.fftN / 2) / env.dsr;
        var out = new Array(frames.length);
        for (i = 0; i < frames.length; i++) out[i] = frames[i] * env.hop / env.dsr + half;
        return out;
    }

    /* Насколько сетка стоит на громких местах: средняя сила огибающей в долях,
       поделённая на среднюю силу вообще И на число долей. Делить на число долей
       обязательно — иначе вдвое более частая сетка выигрывает просто числом
       отсчётов, и выбор уровня выродится в «самый быстрый». */
    function gridScore(env, beats) {
        var flux = env.flux, fps = env.dsr / env.hop, half = (env.fftN / 2) / env.dsr;
        var mean = 0, i;
        for (i = 0; i < flux.length; i++) mean += flux[i];
        mean /= flux.length || 1;
        if (!(mean > 0)) return 0;

        var acc = 0, n = 0;
        for (i = 0; i < beats.length; i++) {
            var f = Math.round((beats[i] - half) * fps);
            if (f < 0 || f >= flux.length) continue;
            /* Окно в один кадр: доля, попавшая между двумя кадрами огибающей,
               не должна считаться промахом — кадр это 16 мс, а слух прощает 50. */
            acc += Math.max(
                flux[f],
                flux[Math.max(0, f - 1)],
                flux[Math.min(flux.length - 1, f + 1)],
            );
            n++;
        }
        return n ? (acc / n) / mean : 0;
    }

    /* Приоритет темпа — тот же, что внутри детектора: центр 120, sigma log2 = 1.
       Он и есть лекарство от смещения счёта к половинному темпу. */
    function tempoPrior(bpm) {
        var r = Math.log(bpm / 120) / Math.LN2;
        return Math.exp(-0.5 * r * r);
    }

    function medianInterval(beats) {
        if (!beats || beats.length < 3) return null;
        var d = [];
        for (var i = 1; i < beats.length; i++) d.push(beats[i] - beats[i - 1]);
        d.sort(function (a, b) { return a - b; });
        return d[Math.floor(d.length / 2)];
    }

    /* Доля долей, рядом с которыми реально что-то прозвучало. Это НЕ уверенность
       в темпе (см. шапку: неверный уровень тоже даёт 91 %), а ответ на вопрос
       «сетка вообще про эту запись?» — по нему тул решает, браться ли. */
    function alignment(beats, onsets) {
        if (!beats || !beats.length || !onsets || !onsets.length) return 0;
        var hit = 0, j = 0;
        for (var i = 0; i < beats.length; i++) {
            while (j + 1 < onsets.length && onsets[j + 1] <= beats[i]) j++;
            var near = Math.abs(onsets[j] - beats[i]);
            if (j + 1 < onsets.length) near = Math.min(near, Math.abs(onsets[j + 1] - beats[i]));
            if (near <= HIT_S) hit++;
        }
        return hit / beats.length;
    }

    function track(ab, opts) {
        var o = opts || {};
        var env = envelopeOf(ab);
        if (!env) return null;

        var seed = o.bpm;
        if (!(seed > 0)) {
            var det = (typeof window.__detectBPM === 'function') ? window.__detectBPM(ab) : null;
            seed = det && det.bpm > 0 ? det.bpm : 120;
        }
        /* Человек назвал темп явно — уровень не выбираем, он и есть ответ.
           ⚠️ И границы поиска на него НЕ распространяются: BPM_MIN/MAX сужают
           ПЕРЕБОР, а не право человека. Сторож поймал это на ×2 от 106 —
           212 выходило за 210, и явно заданный темп молча возвращал null. */
        var explicit = o.bpm > 0;
        var levels = explicit ? [1] : LEVELS;

        var best = null;
        for (var i = 0; i < levels.length; i++) {
            var bpm = seed * levels[i];
            if (explicit ? (bpm < 20 || bpm > 400) : (bpm < BPM_MIN || bpm > BPM_MAX)) continue;
            var beats = trackAt(env, 60 / bpm);
            if (!beats) continue;
            var score = gridScore(env, beats) * tempoPrior(bpm);
            if (!best || score > best.score) {
                best = { beats: beats, score: score, level: levels[i] };
            }
        }
        if (!best) return null;

        var onsets = (typeof window.__detectOnsets === 'function')
            ? window.__detectOnsets(ab).map(function (x) { return x.t; })
            : [];
        var ibi = medianInterval(best.beats);

        return {
            beats: best.beats,
            bpm: ibi ? Math.round(600 / ibi) / 10 : null,
            level: best.level,
            alignment: Math.round(alignment(best.beats, onsets) * 100) / 100,
        };
    }

    window.BeatGrid = { track: track, HIT_S: HIT_S };
})();
